/**
 * Theme management.
 * Stores preference in localStorage and applies via data-theme attribute.
 */

import { getLocale, setLocale, Locale } from './i18n';

export type ThemeName = 'default' | 'modern' | 'fun' | 'basic';

const STORAGE_KEY = 'papelitos-theme';
const DEFAULT_THEME: ThemeName = 'default';

export function getTheme(): ThemeName {
  const stored = localStorage.getItem(STORAGE_KEY) as ThemeName | null;
  if (stored && ['default', 'modern', 'fun', 'basic'].includes(stored)) {
    return stored;
  }
  return DEFAULT_THEME;
}

export function setTheme(theme: ThemeName): void {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem(STORAGE_KEY, theme);
  updateThemeSwitcherUI(theme);
}

function updateThemeSwitcherUI(active: ThemeName): void {
  document.querySelectorAll('.theme-switcher button').forEach((btn) => {
    const btnTheme = (btn as HTMLElement).dataset.theme;
    btn.classList.toggle('active', btnTheme === active);
  });
}

function updateLangSwitcherUI(active: Locale): void {
  document.querySelectorAll('.lang-switcher button').forEach((btn) => {
    const btnLang = (btn as HTMLElement).dataset.lang;
    btn.classList.toggle('active', btnLang === active);
  });
}

/**
 * Renders the floating settings bar (theme + language).
 * Call once on app init.
 */
function renderSettingsBar(): void {
  const bar = document.createElement('div');
  bar.className = 'settings-bar';

  // Language switcher
  const langSwitcher = document.createElement('div');
  langSwitcher.className = 'lang-switcher';
  langSwitcher.innerHTML = `
    <button data-lang="en">EN</button>
    <button data-lang="es">ES</button>
  `;
  langSwitcher.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('button');
    if (btn) {
      const lang = btn.dataset.lang as Locale;
      setLocale(lang);
      updateLangSwitcherUI(lang);
      // Re-render current screen to apply translations
      window.dispatchEvent(new CustomEvent('locale-changed'));
    }
  });

  // Theme switcher
  const themeSwitcher = document.createElement('div');
  themeSwitcher.className = 'theme-switcher';
  themeSwitcher.innerHTML = `
    <button data-theme="default">Default</button>
    <button data-theme="modern">Modern</button>
    <button data-theme="fun">Fun</button>
    <button data-theme="basic">Basic</button>
  `;
  themeSwitcher.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('button');
    if (btn) {
      const theme = btn.dataset.theme as ThemeName;
      setTheme(theme);
    }
  });

  bar.appendChild(langSwitcher);
  bar.appendChild(themeSwitcher);
  document.body.appendChild(bar);
}

/**
 * Initialize theming and language. Call at app startup.
 */
export function initTheme(): void {
  setTheme(getTheme());
  renderSettingsBar();
  // Set initial active states
  updateLangSwitcherUI(getLocale());
}
